// app/(auth)/verify-otp/page.tsx

import React, { useState, useEffect, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OtpInput from "../../../components/auth/OtpInput";
import Button from "../../../components/ui/Button";
import ResendOtp from "../../../components/auth/ResendOtp";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import { verifyOtp, requestOtp } from "../../../lib/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { getErrorMessage } from "../../../lib/utils";
import { ROLES, ROUTES } from "../../../lib/constants";
import toast from "react-hot-toast";
import { sanitizeRedirectUrl, storeRedirectUrl } from "../../../lib/auth";

function VerifyOtpContent() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const router = useNavigate();
  const { login } = useAuth();

  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [devOtpHint, setDevOtpHint] = useState<string | null>(null);

  const phone = searchParams.get("phone");
  const role = searchParams.get("role") as "User" | "Admin" | null;
  const redirect = sanitizeRedirectUrl(searchParams.get("redirect"));

  useEffect(() => {
    if (!phone || !role || !Object.values(ROLES).includes(role)) {
      console.error("Verify OTP: Missing or invalid query parameters.");
      toast.error("Invalid request. Please try logging in again.", {
        id: "verify-otp-param-error",
      });

      const redirectPath =
        role === ROLES.ADMIN ? ROUTES.LOGIN_ADMIN : ROUTES.LOGIN_USER;
      router(redirectPath, { replace: true });
    }
  }, [phone, role, router]);

  useEffect(() => {
    if (!phone || typeof window === "undefined") return;
    const storedOtp = window.sessionStorage.getItem(`local-dev-otp:${phone}`);
    if (storedOtp) {
      setDevOtpHint(storedOtp);
      setOtp(storedOtp);
    }
  }, [phone]);

  const handleOtpChange = (newOtp: string) => {
    setOtp(newOtp);
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phone || !role) {
      setError("Missing phone number or role context. Please restart login.");
      return;
    }
    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await verifyOtp({ phone, otp, role: ROLES.USER });

      toast.success("Login successful!", { id: "login-success" });

      if (redirect) {
        storeRedirectUrl(redirect);
      }

      login(response.token, response.data.user);
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      console.error("OTP Verification Error:", message);
      setError(message);
      toast.error(`Verification failed: ${message}`, { id: "verify-error" });
      setIsLoading(false);
    }
  };

  const handleResendOtp = async (): Promise<boolean> => {
    if (!phone || !role) {
      setResendError("Cannot resend OTP. Phone/Role missing.");
      return false;
    }
    setResendError(null);
    setIsResendLoading(true);
    try {
      const data = await requestOtp({ phone, role });
      const devOtp = data.data?.devOtp;
      if (devOtp && typeof window !== "undefined") {
        window.sessionStorage.setItem(`local-dev-otp:${phone}`, devOtp);
        setDevOtpHint(devOtp);
        setOtp(devOtp);
      }
      toast.success(devOtp ? `Local OTP: ${devOtp}` : "New OTP sent successfully!", { id: "resend-success" });
      setIsResendLoading(false);
      return true;
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      console.error("Resend OTP Error:", message);
      setResendError(message);
      toast.error(`Failed to resend OTP: ${message}`, { id: "resend-error" });
      setIsResendLoading(false);
      return false;
    }
  };

  if (!phone || !role) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-primary p-4">
        <div className="w-full max-w-md text-center">
          <LoadingIndicator size="lg" />
          <p className="text-text-secondary mt-4">Loading details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-primary p-4 relative">
      {/* Ambient background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-golden-accent/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-20 w-80 h-80 rounded-full bg-golden-accent/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-5xl flex rounded-2xl ring-1 ring-dark-border/60 shadow-2xl overflow-hidden bg-dark-secondary/70 backdrop-blur-xl">
        {/* Left Panel: OTP Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12">
          <div className="flex items-center mb-8">
            <img src="/G1.svg" alt="Logo" className="w-10 h-10 mr-3" />
            <span className="text-2xl font-bold tracking-tight text-text-primary">Livelong Wealth</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-text-primary mb-2">Verify OTP</h1>
          <p className="text-text-secondary mb-8 text-sm md:text-base">Enter the 6-digit code sent to {phone}.</p>
          {devOtpHint ? (
            <div className="mb-6 rounded-lg border border-golden-accent/30 bg-golden-accent/10 px-4 py-3 text-sm text-golden-accent">
              Local OTP: <strong>{devOtpHint}</strong>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-6">
            <OtpInput length={6} onChange={handleOtpChange} />

            {error && <ErrorMessage message={error} />}

            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading || otp.length !== 6}
              className="w-full"
              variant="primary"
            >
              Verify OTP & Login
            </Button>
          </form>

          <div className="mt-6 text-center">
            <ResendOtp onResend={handleResendOtp} isLoading={isResendLoading} />
            {resendError && (
              <ErrorMessage message={resendError} className="mt-2 text-xs" />
            )}
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => router(-1)}
              className="text-sm text-golden-accent hover:text-golden-accent-hover hover:underline"
            >
              Change Phone Number?
            </button>
          </div>
        </div>

        {/* Right Panel: Welcome / Info */}
        <div className="hidden md:flex flex-col items-center justify-center w-1/2 bg-gradient-to-br from-dark-tertiary to-dark-secondary p-10 text-text-primary relative">
          <div className="absolute inset-0 flex items-center justify-center opacity-30">
            <div className="w-72 h-72 rounded-full border border-dark-border/60" />
            <div className="absolute w-44 h-44 rounded-full border border-dark-border/50" />
          </div>
          <div className="relative flex flex-col items-center">
            <div className="bg-dark-border/40 rounded-2xl p-6 mb-6 ring-1 ring-dark-border">
              <img src="/G1.svg" alt="OTP Icon" className="w-16 h-16" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-center">Secure Verification</h2>
            <p className="text-center text-base font-medium text-text-secondary max-w-sm">
              For your safety, we verify your identity with a one-time password. This only takes a moment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VerifyOtpPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-dark-primary">
          <LoadingIndicator size="lg" text="Loading..." />
        </div>
      }
    >
      <VerifyOtpContent />
    </Suspense>
  );
}
